import { createSelector } from '@reduxjs/toolkit';
import { selectAssignmentsArrayWithoutDeleted } from 'store/assignments/selectors/assignment.selectors';
import {
  selectAssignmentCosts,
  selectAdditionalWorksArray,
  selectEmployerResourceTree,
  selectEmployerResourceTreeTotals,
  selectParentAndSubProjects,
} from 'scenes/ExpenseTracking/store/selectors/expense-tracking.selectors';
import { selectProjectAssignments } from 'scenes/ExpenseTracking/store/selectors/expense-tracking.selectors';
import {
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import {
  calculateAdditionalWorkTotals,
  calculateAssignmentCosts,
  calculateInvoiceTotals,
  roundTo2Decimals,
} from 'scenes/ExpenseTracking/utils/calculateValues';
import { groupRulesByDestination } from 'store/invoices/utils/invoiceSortRuleGrouping';

const selectInvoicesState = (state) => state.invoices;

// { urls, fetchedAt, loading } for a purchase invoice's signed PDF links.
export const selectPurchaseInvoicePdfUrlsEntry = (state, invoiceId) =>
  state.invoices.pdfUrlsByInvoiceId?.[invoiceId];

export const selectInvoiceScanningAddress = createSelector(
  [selectInvoicesState],
  (state) => state.invoiceScanningAddress || null
);

export const selectInvoices = createSelector(
  [selectInvoicesState],
  (state) => state.invoices.entities
);

const selectInvoiceRows = createSelector(
  [selectInvoicesState],
  (state) => state.invoiceRows
);

export const selectInvoice = (invoiceId) =>
  createSelector([selectInvoices], (invoices) => invoices[invoiceId]);

const selectExpenseTrackingFilters = (state) =>
  state.expenseTracking.activeFilters;

export const selectFilteredInvoices = createSelector(
  [selectInvoices, selectExpenseTrackingFilters, selectInvoiceRows],
  (invoices, activeFilters, rows) => {
    // const invoicesWithRows = Object.values(invoices).map((invoice) => {
    //   return {
    //     ...invoice,
    //     ...(rows[invoice.id] && { customRows: rows[invoice.id] }),
    //   };
    // });
    const invoicesWithRows = invoices;
    let filteredInvoices = { ...invoicesWithRows };
    if (activeFilters.includes('noAssignment')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) => !invoice.assignment || invoice.assignment === 'none'
      );
    }
    if (activeFilters.includes('noProject')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) => !invoice.project
      );
    }
    if (activeFilters.includes('noCategory')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) => !invoice.category
      );
    }
    if (activeFilters.includes('noApproved')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) => invoice.approvalStatus !== 'APPROVED'
      );
    }
    if (activeFilters.includes('noRejected')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) => invoice.approvalStatus !== 'REJECTED'
      );
    }
    if (activeFilters.includes('noPending')) {
      filteredInvoices = Object.values(filteredInvoices).filter(
        (invoice) =>
          invoice.approvalStatus === 'APPROVED' ||
          invoice.approvalStatus === 'REJECTED'
      );
    }

    return filteredInvoices;
  }
);

const selectInvoiceSortRules = createSelector(
  [selectInvoicesState],
  (state) => state.invoiceSortRules.entities
);

export const selectInvoiceSortRulesArray = createSelector(
  [selectInvoiceSortRules],
  (invoiceSortRules) => Object.values(invoiceSortRules)
);

export const selectInvoiceSortRulesByDestination = createSelector(
  [
    selectInvoiceSortRulesArray,
    selectParentAndSubProjects,
    selectProjectAssignments,
  ],
  (invoiceSortRules, projects, assignments) => {
    return groupRulesByDestination(invoiceSortRules, projects, assignments);
  }
);

export const selectInvoiceSortRulesByDestinationByBusiness = (businessId) =>
  createSelector(
    [
      selectInvoiceSortRulesArray,
      selectParentAndSubProjects,
      selectProjectAssignments,
    ],
    (invoiceSortRules, projects, assignments) => {
      const rules = invoiceSortRules.filter(
        (rule) =>
          (rule.keyBusinessId && rule.keyBusinessId === businessId) ||
          // Bid-fix rules are shown under both the mistyped (keyBusinessId)
          // and the corrected company, so they can be found and deleted from
          // the company whose invoices they now produce
          (rule.destinationCompanyBid &&
            rule.destinationCompanyBid === businessId)
      );
      return groupRulesByDestination(rules, projects, assignments);
    }
  );

export const selectInvoiceSortRulesByDestinationByProject = (projectId) =>
  createSelector(
    [
      selectInvoiceSortRulesArray,
      selectParentAndSubProjects,
      selectProjectAssignments,
    ],
    (invoiceSortRules, projects, assignments) => {
      const rules = invoiceSortRules.filter(
        (rule) =>
          (rule.keyProject && rule.keyProject === projectId) ||
          // Bid-fix rules have no keyProject — they apply to the whole main
          // project, so they are shown in every project view of it
          rule.destinationCompanyBid
      );
      return groupRulesByDestination(rules, projects, assignments);
    }
  );

export const selectInvoiceSortRulesByDestinationByProjectByBusiness = (
  projectId,
  businessId
) =>
  createSelector(
    [
      selectInvoiceSortRulesArray,
      selectParentAndSubProjects,
      selectProjectAssignments,
    ],
    (invoiceSortRules, projects, assignments) => {
      const rules = invoiceSortRules.filter(
        (rule) =>
          (rule.keyBusinessId &&
            rule.keyProject &&
            rule.keyProject === projectId &&
            rule.keyBusinessId === businessId) ||
          // Bid-fix rules have no keyProject; shown for both the mistyped
          // and the corrected company of the whole main project
          (rule.destinationCompanyBid &&
            (rule.keyBusinessId === businessId ||
              rule.destinationCompanyBid === businessId))
      );
      return groupRulesByDestination(rules, projects, assignments);
    }
  );

// export const selectInvoiceSortRulesByDestinationByBusiness = (businessId) =>
//   createSelector([selectInvoiceSortRulesByDestination], (invoiceSortRules) => {
//     return invoiceSortRules.filter(
//       (rule) => rule.keyType === 'BUSINESS_ID' && rule.key === businessId
//     );
//   });

export const selectSaleInvoices = createSelector(
  [selectInvoicesState],
  (state) => state.saleInvoices.entities
);

export const selectInvoicesArray = createSelector(
  [selectFilteredInvoices],
  (invoices) =>
    Object.values(invoices).map((invoice) => ({
      ...invoice,
      __typeName: 'Invoice',
      sum: Number(invoice.sum || 0),
      sum_tax: Number(invoice.sum_tax || 0),
    }))
);

export const selectSaleInvoicesArray = createSelector(
  [selectSaleInvoices],
  (invoices) => Object.values(invoices)
);

export const selectInvoicesWithAssignmentData = createSelector(
  [selectInvoicesArray, selectAssignmentsArrayWithoutDeleted],
  (invoices, assignments) => {
    const invoicesWithAssignments = invoices.map((invoice) => {
      const matchedAssignment = assignments.filter(
        (assignment) => invoice.assignment === assignment.id
      )[0];

      return {
        ...invoice,
        assignment: matchedAssignment ? matchedAssignment : invoice.assignment,
      };
    });

    return invoicesWithAssignments;
  }
);

export const selectIsInvoicesLoaded = createSelector(
  [selectInvoicesState],
  (state) => state.isInvoicesLoaded
);

export const selectMaterialInvoices = createSelector(
  [selectFilteredInvoices, selectSelectedProjectId],
  (invoices, projectId) => {
    const category = 'MATERIAL';
    let invoicesArray = Object.values(invoices).filter(
      (invoice) =>
        (invoice.category === category || !invoice.category) &&
        invoice.sortBy !== 'percentage'
    );
    Object.values(invoices)
      .filter((invoice) => invoice.sortBy === 'percentage')
      .forEach((invoice) => {
        const hasBlockForCategoryAndProject =
          invoice?.customRows?.some(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          ) || false;

        if (hasBlockForCategoryAndProject) {
          const blocksWithCategory = invoice.customRows?.filter(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          );
          const invoiceWithOnlyCategoryBlocks = {
            ...invoice,
            customRows: blocksWithCategory,
            sum: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
            sum_tax: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
          };
          invoicesArray.push(invoiceWithOnlyCategoryBlocks);
        }
      });

    return invoicesArray.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
);

export const selectMaterialInvoicesByCompany = createSelector(
  [selectMaterialInvoices],
  (invoices) => {
    return invoices.reduce((acc, invoice) => {
      const company_bid = invoice.company_bid || 'unsorted';
      const company_name = invoice.company_bid
        ? invoice.company_name
        : 'unsorted';
      if (!acc[company_bid]) {
        acc[company_bid] = {
          name: company_name,
          business_id: company_bid,
          invoices: [],
          sum: 0,
        };
      }
      acc[company_bid].invoices.push(invoice);
      acc[company_bid].sum = acc[company_bid].invoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      );
      return acc;
    }, {});
  }
);

export const selectSubcontractorInvoices = createSelector(
  [selectFilteredInvoices, selectSelectedProjectId],
  (invoices, projectId) => {
    const category = 'SUBCONTRACTORS';
    let invoicesArray = Object.values(invoices).filter(
      (invoice) =>
        invoice.category === category && invoice.sortBy !== 'percentage'
    );
    Object.values(invoices)
      .filter((invoice) => invoice.sortBy === 'percentage')
      .forEach((invoice) => {
        const hasBlockForCategoryAndProject =
          invoice?.customRows?.some(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          ) || false;

        if (hasBlockForCategoryAndProject) {
          const blocksWithCategory = invoice.customRows?.filter(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          );
          const invoiceWithOnlyCategoryBlocks = {
            ...invoice,
            customRows: blocksWithCategory,
            sum: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
            sum_tax: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
          };
          invoicesArray.push(invoiceWithOnlyCategoryBlocks);
        }
      });

    return invoicesArray.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
);

export const selectSubcontractorInvoicesByCompany = createSelector(
  [selectSubcontractorInvoices],
  (invoices) => {
    return invoices.reduce((acc, invoice) => {
      const company_bid = invoice.company_bid || 'unsorted';
      const company_name = invoice.company_bid
        ? invoice.company_name
        : 'unsorted';
      if (!acc[company_bid]) {
        acc[company_bid] = {
          name: company_name,
          business_id: company_bid,
          invoices: [],
          sum: 0,
        };
      }
      acc[company_bid].invoices.push(invoice);
      acc[company_bid].sum = acc[company_bid].invoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      );
      return acc;
    }, {});
  }
);

export const selectWasteInvoices = createSelector(
  [selectFilteredInvoices, selectSelectedProjectId],
  (invoices, projectId) => {
    const category = 'WASTE';
    let invoicesArray = Object.values(invoices).filter(
      (invoice) =>
        invoice.category === category && invoice.sortBy !== 'percentage'
    );
    Object.values(invoices)
      .filter((invoice) => invoice.sortBy === 'percentage')
      .forEach((invoice) => {
        const hasBlockForCategoryAndProject =
          invoice?.customRows?.some(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          ) || false;

        if (hasBlockForCategoryAndProject) {
          const blocksWithCategory = invoice.customRows?.filter(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          );
          const invoiceWithOnlyCategoryBlocks = {
            ...invoice,
            customRows: blocksWithCategory,
            sum: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
            sum_tax: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
          };
          invoicesArray.push(invoiceWithOnlyCategoryBlocks);
        }
      });

    return invoicesArray.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
);

export const selectWasteInvoicesByCompany = createSelector(
  [selectWasteInvoices],
  (invoices) => {
    return invoices.reduce((acc, invoice) => {
      const company_bid = invoice.company_bid || 'unsorted';
      const company_name = invoice.company_bid
        ? invoice.company_name
        : 'unsorted';
      if (!acc[company_bid]) {
        acc[company_bid] = {
          name: company_name,
          business_id: company_bid,
          invoices: [],
          sum: 0,
        };
      }
      acc[company_bid].invoices.push(invoice);
      acc[company_bid].sum = acc[company_bid].invoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      );
      return acc;
    }, {});
  }
);

export const selectRentalInvoices = createSelector(
  [selectFilteredInvoices, selectSelectedProjectId],
  (invoices, projectId) => {
    const category = 'RENTAL';
    let invoicesArray = Object.values(invoices).filter(
      (invoice) =>
        invoice.category === category && invoice.sortBy !== 'percentage'
    );
    Object.values(invoices)
      .filter((invoice) => invoice.sortBy === 'percentage')
      .forEach((invoice) => {
        const hasBlockForCategoryAndProject =
          invoice?.customRows?.some(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          ) || false;

        if (hasBlockForCategoryAndProject) {
          const blocksWithCategory = invoice.customRows?.filter(
            (row) =>
              row.category === category &&
              (row.project === projectId || row.mainProject === projectId)
          );
          const invoiceWithOnlyCategoryBlocks = {
            ...invoice,
            customRows: blocksWithCategory,
            sum: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
            sum_tax: blocksWithCategory.reduce(
              (sum, block) => sum + Number(block.sum),
              0
            ),
          };
          invoicesArray.push(invoiceWithOnlyCategoryBlocks);
        }
      });

    return invoicesArray.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
);

export const selectRentalInvoicesByCompany = createSelector(
  [selectRentalInvoices],
  (invoices) => {
    return invoices.reduce((acc, invoice) => {
      const company_bid = invoice.company_bid || 'unsorted';
      const company_name = invoice.company_bid
        ? invoice.company_name
        : 'unsorted';
      if (!acc[company_bid]) {
        acc[company_bid] = {
          name: company_name,
          business_id: company_bid,
          invoices: [],
          sum: 0,
        };
      }
      acc[company_bid].invoices.push(invoice);
      acc[company_bid].sum = acc[company_bid].invoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      );
      return acc;
    }, {});
  }
);

export const selectInvoicesByCategory = createSelector(
  [
    selectMaterialInvoices,
    selectSubcontractorInvoices,
    selectWasteInvoices,
    selectRentalInvoices,
  ],
  (materialInvoices, subcontractorInvoices, wasteInvoices, rentalInvoices) => {
    const material = {
      sum: materialInvoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      ),
      invoices: materialInvoices,
      key: 'MATERIAL',
      name: 'Materiaalit',
    };
    const subcontractors = {
      sum: subcontractorInvoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      ),
      invoices: subcontractorInvoices,
      key: 'SUBCONTRACTORS',
      name: 'Alihankkijat',
    };
    const waste = {
      sum: wasteInvoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      ),
      invoices: wasteInvoices,
      key: 'WASTE',
      name: 'Jätteet',
    };
    const rental = {
      sum: rentalInvoices.reduce(
        (acc, invoice) => acc + Number(invoice.sum || 0),
        0
      ),
      invoices: rentalInvoices,
      key: 'RENTAL',
      name: 'Vuokrat',
    };

    return {
      MATERIAL: material,
      SUBCONTRACTORS: subcontractors,
      WASTE: waste,
      RENTAL: rental,
    };
  }
);

export const selectExpensesByCategory = createSelector(
  [
    selectMaterialInvoices,
    selectSubcontractorInvoices,
    selectWasteInvoices,
    selectRentalInvoices,
    selectEmployerResourceTree,
    selectSelectedProject,
  ],
  (
    materialInvoices,
    subcontractorInvoices,
    wasteInvoices,
    rentalInvoices,
    resourceTree,
    selectedProject
  ) => {
    const material = {
      sum: calculateInvoiceTotals(materialInvoices).sum,
      invoices: materialInvoices,
      key: 'MATERIAL',
      name: 'Materiaalit',
    };
    const subcontractors = {
      sum: calculateInvoiceTotals(subcontractorInvoices).sum,
      invoices: subcontractorInvoices,
      key: 'SUBCONTRACTORS',
      name: 'Alihankkijat',
    };
    const waste = {
      sum: calculateInvoiceTotals(wasteInvoices).sum,
      invoices: wasteInvoices,
      key: 'WASTE',
      name: 'Jätteet',
    };
    const rental = {
      sum: calculateInvoiceTotals(rentalInvoices).sum,
      invoices: rentalInvoices,
      key: 'RENTAL',
      name: 'Vuokrat',
    };

    const filteredResourceTree = resourceTree.filter(
      (employer) => employer.business_id === selectedProject.business_id
    );

    const totalCost = roundTo2Decimals(
      filteredResourceTree.reduce((acc, employer) => {
        return acc + employer.cost;
      }, 0)
    );

    const workHours = {
      sum: totalCost,
      invoices: [],
      key: 'WORKHOURS',
      name: 'Työtunnit',
    };

    return {
      MATERIAL: material,
      SUBCONTRACTORS: subcontractors,
      WASTE: waste,
      RENTAL: rental,
      WORKHOURS: workHours,
    };
  }
);

export const selectInvoicesByProject = createSelector(
  [selectFilteredInvoices, selectParentAndSubProjects],
  (invoices, projects) => {
    invoices = Object.values(invoices);
    projects = Object.values(projects);

    // Initialize accumulator with objects for all projects
    const acc = projects.reduce((acc, project) => {
      acc[project.id] = {
        ...project,
        invoices: [],
        invoiceAmount: 0,
        companies: {},
        sum: 0,
        taxFreeSum: 0,
      };
      return acc;
    }, {});

    const invoicesWithRowsHandled = [];

    invoices.forEach((invoice) => {
      if (invoice.sortBy === 'percentage') {
        invoice.customRows.map((row) => {
          const rowInvoice = {
            ...invoice,
            ...row,
            assignment: row.assignment,
            project: row.project,
            category: row.category,
            sum: row.sum,
            sum_tax: row.sum,

            type: 'row',
          };
          invoicesWithRowsHandled.push(rowInvoice);
        });
      } else {
        invoicesWithRowsHandled.push(invoice);
      }
    });

    // Check if there are invoices without a project ID
    const hasUnsortedInvoices = invoicesWithRowsHandled.some(
      (invoice) => !invoice.project
    );
    const hasInvoicesWithDeletedProjects = invoicesWithRowsHandled.some(
      (invoice) => !projects.some((project) => project.id === invoice.project)
    );

    // Add an object for unsorted invoices only if there are invoices without a project ID or with a nonexisting project
    if (hasUnsortedInvoices || hasInvoicesWithDeletedProjects) {
      acc['unsorted'] = {
        name: 'Lajittelemattomat',
        invoices: [],
        invoiceAmount: 0,
        companies: {},
        sum: 0,
        taxFreeSum: 0,
      };
    }

    // Populate accumulator with invoices and company data
    invoicesWithRowsHandled.forEach((invoice) => {
      let projectId = invoice.project || 'unsorted';

      if (
        projectId !== 'unsorted' &&
        !projects.some((project) => project.id === invoice.project)
      ) {
        projectId = 'unsorted';
      }
      // Add invoice to project or unsorted
      // acc[projectId].invoices.push(invoice);

      acc[projectId].invoiceAmount += 1;

      // Update sum for project or unsorted

      const taxFreeSum = parseFloat(invoice.sum) || 0;
      const sum = parseFloat(invoice.sum_tax) || 0;

      let addedTaxSum = 0;
      if (taxFreeSum === sum) {
        addedTaxSum = taxFreeSum * 0.255;
      } else {
        addedTaxSum = sum;
      }

      acc[projectId].taxFreeSum += Number(invoice.sum || 0);
      acc[projectId].sum += Number(addedTaxSum || 0);

      const company_bid = invoice.company_bid || 'unsorted';
      const company_name = invoice.company_bid
        ? invoice.company_name
        : 'unsorted';

      // Add company data
      if (!acc[projectId].companies[company_bid]) {
        acc[projectId].companies[company_bid] = {
          name: company_name,
          business_id: company_bid,
          invoices: [],
          sum: 0,
        };
      }

      // Add invoice to company
      acc[projectId].companies[company_bid].invoices.push(invoice);

      // Update sum for company
      acc[projectId].companies[company_bid].sum += Number(invoice.sum || 0);
    });

    const filteredProjects = Object.fromEntries(
      Object.entries(acc).filter(([_, project]) => project.invoiceAmount > 0)
    );

    return filteredProjects;
  }
);

export const selectInvoicesByAssignment = createSelector(
  [selectFilteredInvoices, selectAssignmentCosts, selectSelectedProjectId],
  (invoices, assignments, projectId) => {
    invoices = Object.values(invoices);
    assignments = Object.values(assignments).filter(
      (assignment) => assignment.project === projectId
    );

    const assignmentMap = assignments.reduce((acc, assignment) => {
      acc[assignment.id] = assignment;
      return acc;
    }, {});

    // Initialize accumulator with objects for all assignments
    console.log('assignments', assignments);
    const acc = assignments.reduce((acc, assignment) => {
      const { taxFreeSum, taxAmount, sum } = calculateAssignmentCosts(
        assignment,
        []
      );
      acc[assignment.id] = {
        ...assignment,
        invoices: [],
        ownInvoices: [],
        companies: {},
        ownCompanies: {},
        sum: taxFreeSum || 0,
        workCost: assignment.workCost || 0,
        otherCost: assignment.otherCost || 0,
        additionalWorkTaxFreeSum: 0,
        additionalWorkTaxAmount: 0,
        additionalWorkTotalSum: 0,
        additionalWorkOverhead: 0,
      };
      return acc;
    }, {});

    // console.log('acc', acc);

    // Helper function to recursively collect invoices for an assignment and its children
    const collectInvoicesForAssignment = (assignmentId) => {
      const tempInvoices = [];
      const childAssignments = assignments.filter(
        (a) => a.parent === assignmentId
      );

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === assignmentId ||
            (assignmentId === 'unsorted' &&
              !assignmentMap[invoice.assignment])) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasRowForAssignment =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            ) || false;

          if (hasRowForAssignment) {
            const rowsWithAssignment = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentRows = {
              ...invoice,
              invoiceRows: rowsWithAssignment,
              sum: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            tempInvoices.push(invoiceWithOnlyAssignmentRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasBlockForAssignment =
            invoice?.customRows?.some(
              (row) =>
                (row.assignment === assignmentId ||
                  (assignmentId === 'unsorted' &&
                    !assignmentMap[row.assignment])) &&
                row.project === projectId
            ) || false;

          if (hasBlockForAssignment) {
            const blocksWithAssignment = invoice.customRows?.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentBlocks = {
              ...invoice,
              customRows: blocksWithAssignment,
              sum: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            tempInvoices.push(invoiceWithOnlyAssignmentBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      tempInvoices.push(...invoicesForAssignment);

      // Recursively collect invoices for child assignments
      childAssignments.forEach((childAssignment) => {
        const childInvoices = collectInvoicesForAssignment(childAssignment.id);
        tempInvoices.push(...childInvoices);
      });

      return tempInvoices;
    };

    const collectOwnInvoicesForAssignment = (assignmentId) => {
      const ownInvoices = [];

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === assignmentId ||
            (assignmentId === 'unsorted' &&
              !assignmentMap[invoice.assignment])) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasRowForAssignment =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            ) || false;

          if (hasRowForAssignment) {
            const rowsWithAssignment = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentRows = {
              ...invoice,
              invoiceRows: rowsWithAssignment,
              sum: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            ownInvoices.push(invoiceWithOnlyAssignmentRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasBlockForAssignment =
            invoice?.customRows?.some(
              (row) =>
                (row.assignment === assignmentId ||
                  (assignmentId === 'unsorted' &&
                    !assignmentMap[row.assignment])) &&
                row.project === projectId
            ) || false;

          if (hasBlockForAssignment) {
            const blocksWithAssignment = invoice.customRows?.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentBlocks = {
              ...invoice,
              customRows: blocksWithAssignment,
              sum: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            ownInvoices.push(invoiceWithOnlyAssignmentBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      ownInvoices.push(...invoicesForAssignment);

      return ownInvoices;
    };

    const collectUnassignedInvoices = () => {
      const unsortedRowInvoices = [];

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === 'unsorted' ||
            !invoice.assignment ||
            !assignmentMap[invoice.assignment]) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasUnsortedRows =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === 'unsorted' ||
                !row.assignment ||
                !assignmentMap[row.assignment]
            ) || false;

          if (hasUnsortedRows) {
            const unsortedRows = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === 'unsorted' ||
                !row.assignment ||
                !assignmentMap[row.assignment]
            );
            const invoiceWithOnlyUnsortedRows = {
              ...invoice,
              invoiceRows: unsortedRows,
              sum: unsortedRows.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: unsortedRows.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            unsortedRowInvoices.push(invoiceWithOnlyUnsortedRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasUnsortedBlocks =
            invoice?.customRows?.some(
              (block) =>
                (block.assignment === 'unsorted' ||
                  !block.assignment ||
                  !assignmentMap[block.assignment]) &&
                block.project === projectId
            ) || false;

          if (hasUnsortedBlocks) {
            const unsortedBlocks = invoice.customRows?.filter(
              (block) =>
                block.assignment === 'unsorted' ||
                !block.assignment ||
                !assignmentMap[block.assignment]
            );
            const invoiceWithOnlyUnsortedBlocks = {
              ...invoice,
              customRows: unsortedBlocks,
              sum: unsortedBlocks.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: unsortedBlocks.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            unsortedRowInvoices.push(invoiceWithOnlyUnsortedBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      unsortedRowInvoices.push(...invoicesForAssignment);

      return unsortedRowInvoices;
    };

    // Populate accumulator with invoices and company data for each assignment
    assignments.forEach((assignment) => {
      const invoices = collectInvoicesForAssignment(assignment.id);
      const ownInvoices = collectOwnInvoicesForAssignment(assignment.id);

      // Add collected invoices to the assignment's ownInvoices
      acc[assignment.id].invoices = invoices;
      acc[assignment.id].ownInvoices = ownInvoices;

      ownInvoices.forEach((invoice) => {
        const company_bid = invoice.company_bid || 'unsorted';
        const company_name = invoice.company_bid
          ? invoice.company_name
          : 'unsorted';
        if (!acc[assignment.id].ownCompanies[company_bid]) {
          acc[assignment.id].ownCompanies[company_bid] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].ownCompanies[company_bid].invoices.push(invoice);
        acc[assignment.id].ownCompanies[company_bid].sum += Number(
          invoice.sum || 0
        );
      });

      // Collect company data for the current assignment's companies
      invoices.forEach((invoice) => {
        const company_bid = invoice.company_bid || 'unsorted';
        const company_name = invoice.company_bid
          ? invoice.company_name
          : 'unsorted';
        if (!acc[assignment.id].companies[company_bid]) {
          acc[assignment.id].companies[company_bid] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].companies[company_bid].invoices.push(invoice);
        acc[assignment.id].companies[company_bid].sum += Number(
          invoice.sum || 0
        );
      });

      // Update sum for the current assignment
      acc[assignment.id].sum = invoices.reduce(
        (sum, invoice) => sum + Number(invoice.sum || 0),
        0
      );
    });

    // Include invoices without assignment in the "unsorted" assignment
    // const unassignedInvoices = collectUnassignedInvoices();
    // if (unassignedInvoices.length > 0) {
    //   // acc['unsorted'] = {
    //   //   id: 'unsorted',
    //   //   name: 'Lajittelemattomat',
    //   //   invoices: unassignedInvoices,
    //   //   ownInvoices: unassignedInvoices,
    //   //   companies: {},
    //   //   ownCompanies: {},
    //   //   sum: unassignedInvoices.reduce(
    //   //     (sum, invoice) => sum + Number(invoice.sum || 0),
    //   //     0
    //   //   ),
    //   // };

    //   acc["unsorted"].invoices = unassignedInvoices;
    //   acc["unsorted"].ownInvoices = unassignedInvoices;

    //   // Collect company data for unsorted invoices
    //   unassignedInvoices.forEach((invoice) => {
    //     const company_bid = invoice.company_bid || 'unsorted';
    //     const company_name = invoice.company_bid
    //       ? invoice.company_name
    //       : 'unsorted';
    //     if (!acc['unsorted'].companies[company_bid]) {
    //       acc['unsorted'].companies[company_bid] = {
    //         name: company_name,
    //         business_id: company_bid,
    //         invoices: [],
    //         sum: 0,
    //       };
    //     }
    //     acc['unsorted'].companies[company_bid].invoices.push(invoice);
    //     acc['unsorted'].companies[company_bid].sum += Number(invoice.sum || 0);

    //     if (!acc['unsorted'].ownCompanies[company_bid]) {
    //       acc['unsorted'].ownCompanies[company_bid] = {
    //         name: company_name,
    //         business_id: company_bid,
    //         invoices: [],
    //         sum: 0,
    //       };
    //     }
    //     acc['unsorted'].ownCompanies[company_bid].invoices.push(invoice);
    //     acc['unsorted'].ownCompanies[company_bid].sum += Number(
    //       invoice.sum || 0
    //     );
    //   });
    // }

    acc['leftover'] = {
      id: 'leftover',
      name: 'Ylijäämä',
      invoices: [],
      ownInvoices: [],
      companies: {},
      ownCompanies: {},
      sum: 0,
    };

    Object.values(acc).forEach((assignment) => {
      acc[assignment.id] = {
        ...assignment,
        ...countAssignmentValues(assignment),
      };
    });
    console.log('acc in the end', acc);
    return acc;
  }
);

// The littera list in expense tracking used to come only from the
// assignment-costs endpoint. That endpoint is slow (it parses payroll Excels
// and walks the project's records) and read a single DynamoDB page, so the
// list could arrive late, partial or empty while the rest of the page was
// fine. The list is now seeded from the project's own assignments, which load
// separately and paginated, and the cost figures are merged in when they
// arrive.
export const mergeProjectAssignmentsWithCosts = (
  projectAssignments,
  assignmentCosts,
  projectId
) => {
  const byId = {};
  Object.values(projectAssignments || {}).forEach((assignment) => {
    if (
      assignment &&
      assignment.project === projectId &&
      !assignment.deletedAt
    ) {
      byId[assignment.id] = assignment;
    }
  });
  Object.values(assignmentCosts || {}).forEach((assignment) => {
    if (!assignment || assignment.project !== projectId) return;
    // Descriptive fields come from the assignment store, which is updated
    // after edits; only the computed cost figures are taken from the costs.
    byId[assignment.id] = {
      ...(byId[assignment.id] || assignment),
      workCost: assignment.workCost,
      workHours: assignment.workHours,
      otherCost: assignment.otherCost,
    };
  });
  if (!byId.unsorted) {
    byId.unsorted = {
      id: 'unsorted',
      name: 'Lajittelemattomat',
      project: projectId,
    };
  }
  return Object.values(byId);
};

export const selectCostsByAssignmentAndCategory = createSelector(
  [
    selectFilteredInvoices,
    selectAssignmentCosts,
    selectProjectAssignments,
    selectSelectedProjectId,
    selectAdditionalWorksArray,
  ],
  (
    invoices,
    assignmentCosts,
    projectAssignments,
    projectId,
    additionalWorks
  ) => {
    invoices = Object.values(invoices);
    additionalWorks = additionalWorks || [];
    const assignments = mergeProjectAssignmentsWithCosts(
      projectAssignments,
      assignmentCosts,
      projectId
    );

    const assignmentMap = assignments.reduce((acc, assignment) => {
      acc[assignment.id] = assignment;
      return acc;
    }, {});

    // Initialize accumulator with objects for all assignments
    console.log('assignments', assignments);
    const acc = assignments.reduce((acc, assignment) => {
      const { taxFreeSum, taxAmount, sum } = calculateAssignmentCosts(
        assignment,
        []
      );
      acc[assignment.id] = {
        ...assignment,
        invoices: [],
        ownInvoices: [],
        companies: {},
        ownCompanies: {},
        sum: taxFreeSum || 0,
        workCost: assignment.workCost || 0,
        otherCost: assignment.otherCost || 0,
      };
      return acc;
    }, {});

    // console.log('acc', acc);

    // Helper function to recursively collect invoices for an assignment and its children
    const collectInvoicesForAssignment = (assignmentId) => {
      const tempInvoices = [];
      const childAssignments = assignments.filter(
        (a) => a.parent === assignmentId
      );

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === assignmentId ||
            (assignmentId === 'unsorted' &&
              !assignmentMap[invoice.assignment])) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasRowForAssignment =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            ) || false;

          if (hasRowForAssignment) {
            const rowsWithAssignment = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentRows = {
              ...invoice,
              invoiceRows: rowsWithAssignment,
              sum: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            tempInvoices.push(invoiceWithOnlyAssignmentRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasBlockForAssignment =
            invoice?.customRows?.some(
              (row) =>
                (row.assignment === assignmentId ||
                  (assignmentId === 'unsorted' &&
                    !assignmentMap[row.assignment])) &&
                row.project === projectId
            ) || false;

          if (hasBlockForAssignment) {
            const blocksWithAssignment = invoice.customRows?.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentBlocks = {
              ...invoice,
              customRows: blocksWithAssignment,
              sum: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            tempInvoices.push(invoiceWithOnlyAssignmentBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      tempInvoices.push(...invoicesForAssignment);

      // Recursively collect invoices for child assignments
      childAssignments.forEach((childAssignment) => {
        const childInvoices = collectInvoicesForAssignment(childAssignment.id);
        tempInvoices.push(...childInvoices);
      });

      return tempInvoices;
    };

    const collectOwnInvoicesForAssignment = (assignmentId) => {
      const ownInvoices = [];

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === assignmentId ||
            (assignmentId === 'unsorted' &&
              !assignmentMap[invoice.assignment])) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasRowForAssignment =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            ) || false;

          if (hasRowForAssignment) {
            const rowsWithAssignment = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentRows = {
              ...invoice,
              invoiceRows: rowsWithAssignment,
              sum: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            ownInvoices.push(invoiceWithOnlyAssignmentRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasBlockForAssignment =
            invoice?.customRows?.some(
              (row) =>
                (row.assignment === assignmentId ||
                  (assignmentId === 'unsorted' &&
                    !assignmentMap[row.assignment])) &&
                row.project === projectId
            ) || false;

          if (hasBlockForAssignment) {
            const blocksWithAssignment = invoice.customRows?.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentBlocks = {
              ...invoice,
              customRows: blocksWithAssignment,
              sum: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            ownInvoices.push(invoiceWithOnlyAssignmentBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      ownInvoices.push(...invoicesForAssignment);

      return ownInvoices;
    };

    const collectOwnInvoicesUnderCategoriesForAssignment = (assignmentId) => {
      const ownInvoices = [];
      const categories = {
        MATERIAL: {
          invoices: [],
          companies: {},
          sum: 0,
        },
        SUBCONTRACTORS: {
          invoices: [],
          companies: {},
          sum: 0,
        },
        WASTE: {
          invoices: [],
          companies: {},
          sum: 0,
        },
        RENTAL: {
          invoices: [],
          companies: {},
          sum: 0,
        },
        WORKHOURS: {
          invoices: [],
          companies: {},
          sum: 0,
        },
      };

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === assignmentId ||
            (assignmentId === 'unsorted' &&
              !assignmentMap[invoice.assignment])) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      Object.keys(categories).forEach((category) => {
        categories[category].invoices = invoicesForAssignment.filter(
          (invoice) => invoice.category === category
        );
      });

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasRowForAssignment =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            ) || false;

          if (hasRowForAssignment) {
            const rowsWithAssignment = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentRows = {
              ...invoice,
              invoiceRows: rowsWithAssignment,
              sum: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: rowsWithAssignment.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };

            categories[invoice.category || 'MATERIAL'].invoices.push(
              invoiceWithOnlyAssignmentRows
            );
            ownInvoices.push(invoiceWithOnlyAssignmentRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasBlockForAssignment =
            invoice?.customRows?.some(
              (row) =>
                (row.assignment === assignmentId ||
                  (assignmentId === 'unsorted' &&
                    !assignmentMap[row.assignment])) &&
                row.project === projectId
            ) || false;

          if (hasBlockForAssignment) {
            const blocksWithAssignment = invoice.customRows?.filter(
              (row) =>
                row.assignment === assignmentId ||
                (assignmentId === 'unsorted' && !assignmentMap[row.assignment])
            );
            const invoiceWithOnlyAssignmentBlocks = {
              ...invoice,
              customRows: blocksWithAssignment,
              sum: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: blocksWithAssignment.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            categories[invoice.category || 'MATERIAL'].invoices.push(
              invoiceWithOnlyAssignmentBlocks
            );
            ownInvoices.push(invoiceWithOnlyAssignmentBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      ownInvoices.push(...invoicesForAssignment);

      return categories;
    };

    const getAdditionalWorkType = (additionalWork) =>
      additionalWork?.type === 'shareholderChange' ||
      additionalWork?.type === 'SHAREHOLDER_CHANGE'
        ? 'shareholderChange'
        : 'additionalWork';

    const collectOwnAdditionalWorksForAssignment = (
      assignmentId,
      expectedType = 'additionalWork'
    ) => {
      return additionalWorks.reduce(
        (collectedAdditionalWorks, additionalWork) => {
          if (getAdditionalWorkType(additionalWork) !== expectedType) {
            return collectedAdditionalWorks;
          }

          if (additionalWork.status === 'CANCELLED') {
            return collectedAdditionalWorks;
          }

          const additionalWorkRows =
            additionalWork.rows ||
            additionalWork.additionalWorkRows ||
            additionalWork.invoiceRows ||
            [];

          const filteredRows = additionalWorkRows.filter(
            (row) => row.assignment === assignmentId
          );

          if (!filteredRows.length) {
            return collectedAdditionalWorks;
          }

          const totals = calculateAdditionalWorkTotals(filteredRows);

          collectedAdditionalWorks.push({
            ...additionalWork,
            rows: filteredRows,
            taxFreeSum: totals.taxFreeSum,
            taxAmount: totals.taxAmount,
            sum: totals.sum,
            overhead: totals.overhead,
          });

          return collectedAdditionalWorks;
        },
        []
      );
    };

    const collectUnassignedInvoices = () => {
      const unsortedRowInvoices = [];

      // Collect invoices for the current assignment
      const invoicesForAssignment = invoices.filter(
        (invoice) =>
          (invoice.assignment === 'unsorted' ||
            !invoice.assignment ||
            !assignmentMap[invoice.assignment]) &&
          invoice.sortBy !== 'invoiceRow' &&
          invoice.sortBy !== 'percentage'
      );

      // Handle invoices with sortBy === 'invoiceRow' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'invoiceRow')
        .forEach((invoice) => {
          const hasUnsortedRows =
            invoice?.invoiceRows.some(
              (row) =>
                row.assignment === 'unsorted' ||
                !row.assignment ||
                !assignmentMap[row.assignment]
            ) || false;

          if (hasUnsortedRows) {
            const unsortedRows = invoice.invoiceRows.filter(
              (row) =>
                row.assignment === 'unsorted' ||
                !row.assignment ||
                !assignmentMap[row.assignment]
            );
            const invoiceWithOnlyUnsortedRows = {
              ...invoice,
              invoiceRows: unsortedRows,
              sum: unsortedRows.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
              sum_tax: unsortedRows.reduce(
                (sum, row) =>
                  sum + Number(row.quantity) * Number(row.unitPrice),
                0
              ),
            };
            unsortedRowInvoices.push(invoiceWithOnlyUnsortedRows);
          }
        });

      // Handle invoices with sortBy === 'percentage' separately
      invoices
        .filter((invoice) => invoice.sortBy === 'percentage')
        .forEach((invoice) => {
          const hasUnsortedBlocks =
            invoice?.customRows?.some(
              (block) =>
                (block.assignment === 'unsorted' ||
                  !block.assignment ||
                  !assignmentMap[block.assignment]) &&
                block.project === projectId
            ) || false;

          if (hasUnsortedBlocks) {
            const unsortedBlocks = invoice.customRows?.filter(
              (block) =>
                block.assignment === 'unsorted' ||
                !block.assignment ||
                !assignmentMap[block.assignment]
            );
            const invoiceWithOnlyUnsortedBlocks = {
              ...invoice,
              customRows: unsortedBlocks,
              sum: unsortedBlocks.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
              sum_tax: unsortedBlocks.reduce(
                (sum, block) => sum + Number(block.sum),
                0
              ),
            };
            unsortedRowInvoices.push(invoiceWithOnlyUnsortedBlocks);
          }
        });

      // Add collected invoices to the ownInvoices array of the current assignment
      unsortedRowInvoices.push(...invoicesForAssignment);

      return unsortedRowInvoices;
    };

    // Populate accumulator with invoices and company data for each assignment
    assignments.forEach((assignment) => {
      const invoices = collectInvoicesForAssignment(assignment.id);
      const ownInvoices = collectOwnInvoicesForAssignment(assignment.id);
      // const categories = collectOwnInvoicesUnderCategoriesForAssignment(
      //   assignment.id
      // );
      const categories = {
        MATERIAL: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: 0,
          name: 'MATERIAL',
          title: 'material_invoices',
        },
        SUBCONTRACTORS: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: 0,
          name: 'SUBCONTRACTORS',
          title: 'subcontractor_invoices',
        },
        WASTE: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: 0,
          name: 'WASTE',
          title: 'waste_invoices',
        },
        RENTAL: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: 0,
          name: 'RENTAL',
          title: 'rental_invoices',
        },
        WORKHOURS: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: assignment.workCost || 0,
          hours: assignment.workHours || 0,
          name: 'WORKHOURS',
          title: 'workhours',
        },
        OTHER: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          sum: assignment.otherCost || 0,
          hours: 0,
          name: 'OTHER',
          title: 'other',
        },
        ADDITIONAL_WORK: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          additionalWorks: [],
          sum: 0,
          taxAmount: 0,
          totalSum: 0,
          overhead: 0,
          name: 'ADDITIONAL_WORK',
          title: 'additional_work',
        },
        SHAREHOLDER_CHANGE: {
          invoices: [],
          ownInvoices: [],
          companies: {},
          ownCompanies: {},
          additionalWorks: [],
          sum: 0,
          taxAmount: 0,
          totalSum: 0,
          overhead: 0,
          name: 'SHAREHOLDER_CHANGE',
          title: 'shareholder_changes',
        },
      };
      // console.log('categories for assignment', assignment.name, categories);

      // Add collected invoices to the assignment's ownInvoices
      acc[assignment.id].invoices = invoices;
      acc[assignment.id].ownInvoices = ownInvoices;
      acc[assignment.id].categories = categories;

      const assignmentAdditionalWorks = collectOwnAdditionalWorksForAssignment(
        assignment.id,
        'additionalWork'
      );
      const assignmentShareholderChanges =
        collectOwnAdditionalWorksForAssignment(
          assignment.id,
          'shareholderChange'
        );
      const assignmentWorkChanges = [
        ...assignmentAdditionalWorks,
        ...assignmentShareholderChanges,
      ];

      acc[assignment.id].categories.ADDITIONAL_WORK.additionalWorks =
        assignmentAdditionalWorks;
      acc[assignment.id].categories.SHAREHOLDER_CHANGE.additionalWorks =
        assignmentShareholderChanges;

      acc[assignment.id].additionalWorkTaxFreeSum =
        assignmentWorkChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxFreeSum || 0),
          0
        );
      acc[assignment.id].additionalWorkTaxAmount =
        assignmentWorkChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxAmount || 0),
          0
        );
      acc[assignment.id].additionalWorkTotalSum =
        assignmentWorkChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.sum || 0),
          0
        );
      acc[assignment.id].additionalWorkOverhead =
        assignmentWorkChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.overhead || 0),
          0
        );

      acc[assignment.id].categories.ADDITIONAL_WORK.sum =
        assignmentAdditionalWorks.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxFreeSum || 0),
          0
        );
      acc[assignment.id].categories.ADDITIONAL_WORK.taxAmount =
        assignmentAdditionalWorks.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxAmount || 0),
          0
        );
      acc[assignment.id].categories.ADDITIONAL_WORK.totalSum =
        assignmentAdditionalWorks.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.sum || 0),
          0
        );
      acc[assignment.id].categories.ADDITIONAL_WORK.overhead =
        assignmentAdditionalWorks.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.overhead || 0),
          0
        );

      acc[assignment.id].categories.SHAREHOLDER_CHANGE.sum =
        assignmentShareholderChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxFreeSum || 0),
          0
        );
      acc[assignment.id].categories.SHAREHOLDER_CHANGE.taxAmount =
        assignmentShareholderChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.taxAmount || 0),
          0
        );
      acc[assignment.id].categories.SHAREHOLDER_CHANGE.totalSum =
        assignmentShareholderChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.sum || 0),
          0
        );
      acc[assignment.id].categories.SHAREHOLDER_CHANGE.overhead =
        assignmentShareholderChanges.reduce(
          (sum, additionalWork) => sum + Number(additionalWork.overhead || 0),
          0
        );

      ownInvoices.forEach((invoice) => {
        const invoiceCategory = invoice.category || 'MATERIAL';
        const company_bid = invoice.company_bid || 'unsorted';
        const company_name = invoice.company_bid
          ? invoice.company_name
          : 'unsorted';

        if (!acc[assignment.id].ownCompanies[company_bid]) {
          acc[assignment.id].ownCompanies[company_bid] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].ownCompanies[company_bid].invoices.push(invoice);
        acc[assignment.id].ownCompanies[company_bid].sum += Number(
          invoice.sum || 0
        );

        acc[assignment.id].categories[invoiceCategory].ownInvoices.push(
          invoice
        );
        acc[assignment.id].categories[invoiceCategory].sum += Number(
          invoice.sum || 0
        );

        if (
          !acc[assignment.id].categories[invoiceCategory].ownCompanies[
            company_bid
          ]
        ) {
          acc[assignment.id].categories[invoiceCategory].ownCompanies[
            company_bid
          ] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].categories[invoiceCategory].ownCompanies[
          company_bid
        ].invoices.push(invoice);
        acc[assignment.id].categories[invoiceCategory].ownCompanies[
          company_bid
        ].sum += Number(invoice.sum || 0);
      });

      // Collect company data for the current assignment's companies
      invoices.forEach((invoice) => {
        const invoiceCategory = invoice.category || 'MATERIAL';
        const company_bid = invoice.company_bid || 'unsorted';
        const company_name = invoice.company_bid
          ? invoice.company_name
          : 'unsorted';
        if (!acc[assignment.id].companies[company_bid]) {
          acc[assignment.id].companies[company_bid] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].companies[company_bid].invoices.push(invoice);
        acc[assignment.id].companies[company_bid].sum += Number(
          invoice.sum || 0
        );

        acc[assignment.id].categories[invoiceCategory].invoices.push(invoice);
        acc[assignment.id].categories[invoiceCategory].sum += Number(
          invoice.sum || 0
        );

        if (
          !acc[assignment.id].categories[invoiceCategory].companies[company_bid]
        ) {
          acc[assignment.id].categories[invoiceCategory].companies[
            company_bid
          ] = {
            name: company_name,
            business_id: company_bid,
            invoices: [],
            sum: 0,
          };
        }
        acc[assignment.id].categories[invoiceCategory].companies[
          company_bid
        ].invoices.push(invoice);
        acc[assignment.id].categories[invoiceCategory].companies[
          company_bid
        ].sum += Number(invoice.sum || 0);
      });

      // Update sum for the current assignment
      acc[assignment.id].sum =
        invoices.reduce((sum, invoice) => sum + Number(invoice.sum || 0), 0) +
        acc[assignment.id].additionalWorkTaxFreeSum;
    });

    acc['leftover'] = {
      id: 'leftover',
      name: 'Ylijäämä',
      invoices: [],
      ownInvoices: [],
      companies: {},
      ownCompanies: {},
      sum: 0,
    };

    Object.values(acc).forEach((assignment) => {
      acc[assignment.id] = {
        ...assignment,
        ...countAssignmentValues(assignment),
      };
    });
    return acc;
  }
);

export const countAssignmentValues = (assignment) => {
  let tempAssignment = { ...assignment };

  const originalBudget = {
    ...assignment.originalBudget,
    value: assignment.originalBudget?.value || 0,
    comment: assignment.originalBudget?.comment || '',
  };

  const updatedBudget = {
    ...assignment.updatedBudget,
    value: assignment.updatedBudget?.value || 0,
    comment: assignment.updatedBudget?.comment || '',
  };

  const transfer = {
    ...assignment.transfer,
    value: assignment.transfer?.value || 0,
    comment: assignment.transfer?.comment || '',
  };

  const talseComment = assignment.talseComment || '';

  const finalBudget = getFinalBudget(assignment);
  const invoiceCost = getInvoiceCost(assignment);
  const actualCost = getActualCost(assignment);
  const kustannustoteumaSum =
    actualCost && finalBudget ? (actualCost / finalBudget) * 100 : 0;
  const kustannustoteumaLeft =
    actualCost && finalBudget ? finalBudget - actualCost : 0;
  const completion = assignment.completion || 0;
  const incomingExpenses = getIncomingExpenses(assignment);

  const expectedFinalExpenses = Math.round(incomingExpenses + actualCost);
  const subtraction = expectedFinalExpenses - finalBudget;
  tempAssignment = {
    ...tempAssignment,
    originalBudget: originalBudget,
    updatedBudget: updatedBudget,
    transfer: transfer,
    talseComment: talseComment,
    finalBudget: finalBudget,
    invoiceCost: invoiceCost,
    actualCost: actualCost,
    kustannustoteumaSum: kustannustoteumaSum,
    kustannustoteumaLeft: kustannustoteumaLeft,
    completion: completion,
    incomingExpenses: incomingExpenses,
    expectedFinalExpenses: expectedFinalExpenses,
    subtraction: subtraction,
    open: assignment.open || false,
  };
  return tempAssignment;
};

const getIncomingExpenses = (assignment) => {
  if (!assignment) return 0;
  const { completion } = assignment;
  const finalBudget = getFinalBudget(assignment);
  if (finalBudget && completion) return finalBudget * (1 - completion * 0.01);
  return 0;
};

const getFinalBudget = (assignment) => {
  if (!assignment) return 0;
  const { originalBudget, updatedBudget } = assignment;
  if (originalBudget && updatedBudget && updatedBudget.value)
    return Number(updatedBudget.value);
  if (originalBudget && (!updatedBudget || !updatedBudget.value))
    return Number(originalBudget.value);
  if (!originalBudget && updatedBudget && updatedBudget.value)
    return Number(updatedBudget.value);
  return 0;
};

// InvoiceCost has only the cost of all the invoices in the assignment
const getInvoiceCost = (assignment) => {
  if (!assignment) return;

  const workCost = Number(assignment.workCost) || 0;
  const otherCost = Number(assignment.otherCost) || 0;
  const additionalWorkTaxFreeSum =
    Number(assignment.additionalWorkTaxFreeSum) || 0;

  const assignmentInvoicesSum = assignment.ownInvoices
    ?.filter((invoice) => invoice.approvalStatus !== 'REJECTED')
    .reduce((sum, invoice) => {
      const invoiceSum = Number(invoice.sum || 0);
      return sum + invoiceSum;
    }, 0);
  return (
    assignmentInvoicesSum + workCost + otherCost + additionalWorkTaxFreeSum
  );
};

const getTransferCost = (assignment) => {
  if (!assignment) return;
  if (assignment.transfer && assignment.transfer.value) {
    const transferValue = Number(assignment.transfer.value);
    return transferValue;
  } else {
    return 0;
  }
};

// ActualCost has the cost of all the invoices in the assignment, plus the transfer value, plus all the additional costs
const getActualCost = (assignment) => {
  if (!assignment) return;
  const assignmentInvoicesSum =
    getInvoiceCost(assignment) + getTransferCost(assignment);
  return assignmentInvoicesSum;
};
